import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Chip,
    OutlinedInput,
    SelectChangeEvent,
    IconButton,
    Tooltip,
    Button,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useIEPData } from '../contexts';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export const IEPFilterControls = () => {
    const { filter, updateFilter, activeSchoolYear } = useIEPData();

    // Local state for pending changes
    const [tempYears, setTempYears] = useState<number[]>(filter.schoolYears);

    const currentYear = new Date().getFullYear();
    const schoolYearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

    // Sync local state when filter changes externally
    useEffect(() => {
        setTempYears(filter.schoolYears);
    }, [filter.schoolYears]);

    const hasPendingChanges =
        JSON.stringify(tempYears) !== JSON.stringify(filter.schoolYears);

    const handleYearChange = (event: SelectChangeEvent<number[]>) => {
        const value = event.target.value;
        setTempYears(typeof value === 'string' ? value.split(',').map(Number) : value);
    };

    const handleApply = () => {
        updateFilter({
            schoolYears: tempYears,
        });
    };

    const handleClearYears = () => {
        if (activeSchoolYear) {
            const newYears = [activeSchoolYear];
            setTempYears(newYears);
            updateFilter({ schoolYears: newYears });
        }
    };

    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 250 }}>
                    <InputLabel id="iep-school-year-label">School Year</InputLabel>
                    <Select
                        labelId="iep-school-year-label"
                        multiple
                        value={tempYears}
                        onChange={handleYearChange}
                        input={<OutlinedInput label="School Year" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((year) => (
                                    <Chip key={year} label={`${year}-${year + 1}`} size="small" />
                                ))}
                            </Box>
                        )}
                        MenuProps={MenuProps}
                    >
                        {schoolYearOptions.map((year) => (
                            <MenuItem key={year} value={year}>
                                <Checkbox checked={tempYears.indexOf(year) > -1} />
                                <ListItemText primary={`${year}-${year + 1}`} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {hasPendingChanges && (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleApply}
                    >
                        Apply
                    </Button>
                )}

                {filter.schoolYears.length > 1 && !hasPendingChanges && (
                    <Tooltip title="Reset to current year">
                        <IconButton size="small" onClick={handleClearYears}>
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};